import type { MaterialUnit } from 'api/models/mainservice';
import type { MaterialGroupBy } from 'common/models';
import type { TFunction } from 'i18next';
import type { MaterialTableQuery } from 'pages/material/models';
import type { PriceChangeType } from 'pages/material/priceLists/models';
import type { OrderMaterial } from 'pages/orders/models';
import { getI18n } from 'react-i18next';

import { toLocaleString } from './mathHelpers';
import { usePriceTranslate } from './priceHelpers';
import { useCustomTranslation } from './translationHelper';

const MATERIAL_PRICE_COLUMNS_COUNT = 6; // name, identification number, storeName, supplierCode, amount, price
export const MATERIAL_DECIMAL_PLACES = 2;

export const unitName = (unit: string) => {
  return `material.unit.${unit}.name`;
};

export const unitShortcut = (unit: string) => {
  return `material.unit.${unit}.abbr`;
};

export const unitValueShorcut = (unit: string) => {
  return `material.unit.${unit}.value`;
};

export const translateUnitWithValue = (
  t: TFunction,
  unit: MaterialUnit,
  value: number | string
) => {
  return t(unitValueShorcut(unit), { value: toLocaleString(value) });
};

export const useMaterialTranslate = () => {
  const { tCommon, tMaterial } = useCustomTranslation();
  const { translatePrice } = usePriceTranslate();

  const translateUnitName = (unit: MaterialUnit) => tCommon(`unit_${unit}_name`);

  const translateUnitShortcut = (unit: MaterialUnit) => tCommon(`unit_${unit}_abbr`);

  const translateUnitValue = (unit: MaterialUnit, value: number | string) =>
    tCommon(`unit_${unit}_value`, { value });

  const translatePriceChangeType = (type: PriceChangeType) => tMaterial(`price_change_${type}`);

  const translatePriceWithUnit = (unit: MaterialUnit, price: number, currencyId?: string) =>
    `${translatePrice(price, currencyId)}/${translateUnitShortcut(unit)}`;

  return {
    translateUnitName,
    translateUnitShortcut,
    translateUnitValue,
    translatePriceChangeType,
    translatePriceWithUnit
  };
};

// helpers for Material input and Material block

export const getMaterialGroupByKey = (material: OrderMaterial, groupByKey?: MaterialGroupBy) => {
  if (groupByKey === 'category') {
    return material.categoryId;
  }

  return material.storeId;
};

export const getMaterialGroupedRowTitle = (
  material: OrderMaterial,
  groupByKey?: MaterialGroupBy
) => {
  const i18n = getI18n();

  if (groupByKey === 'category') {
    return material.categoryName || i18n.t('customData.material.customItems');
  }

  return material.storeName || i18n.t('customData.material.customItems');
};

export const getMaterialPriceColSpan = (
  visibleColumns?: string[],
  groupByKey?: MaterialGroupBy
) => {
  if (visibleColumns) {
    // we always show amount and price per item
    const columnsCount = visibleColumns.length + 2;

    if (groupByKey) {
      if (
        (groupByKey === 'category' && visibleColumns.includes('categoryName')) ||
        (groupByKey === 'store' && visibleColumns.includes('stroeName'))
      ) {
        return columnsCount - 1;
      }
    }

    return columnsCount;
  }

  if (groupByKey && groupByKey === 'store') return MATERIAL_PRICE_COLUMNS_COUNT - 1;

  return MATERIAL_PRICE_COLUMNS_COUNT;
};

export const isMaterialFiltered = (tableParams: MaterialTableQuery) => {
  if (tableParams.categoryId || tableParams.supplierId || !!tableParams.searchPhrase) return true;

  return false;
};
